// =============================================================================
// ui/state/contracts.tsx — the futures catalogue.
// -----------------------------------------------------------------------------
// Same discipline as accounts and preferences: NULL until a read succeeds, so a
// failed load renders as missing rather than as an empty catalogue.
//
// ⚠ The difference from accounts is that an empty catalogue has NO legitimate
// empty state. A user can genuinely have no accounts; nobody can genuinely have
// no contracts — the shared table is admin-seeded and identical for everyone. So
// an empty array here is always a fault, and `io/sync/contracts.ts` throws on a
// failed read precisely so this provider never has to guess which kind it is.
//
// Loaded once per session at the shell, not per tab. It is 55 shared rows that
// every screen showing money-per-point needs, and re-reading it on each tab
// change would be a request per navigation for data that cannot have changed.
//
// ── 🚨 TWO TABLES, AND ONLY ONE OF THEM MAY BE EMPTY ──
//
// `contracts` is shared, admin-seeded, and world-readable. `user_contracts` is
// the trader's own — instruments the shared list has never heard of, plus
// overrides of rows it has. The two are merged here, once, and every consumer
// downstream sees a single catalogue and cannot tell which table a spec came
// from. That is deliberate: `findContract`'s longest-prefix rule has to run over
// ALL candidates at once, and a two-stage lookup (mine, then theirs) would
// resolve `SILU26` to `SI` the moment a trader owned an `SI` override.
//
// ⚠ Their failure modes are opposite, so they are handled oppositely:
//
//   shared read fails → THROW. Empty is impossible, so empty means broken, and
//                       every consumer degrades to a plausible $0.
//   custom read fails → carry on with the shared list and SAY SO. Empty is the
//                       normal case for almost every trader, so it cannot be
//                       treated as a fault — but a trader who HAS overrides is
//                       now seeing the standard numbers instead of their own,
//                       which is a wrong number rather than a missing one.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { loadContracts } from '../../io/sync/contracts';
import { loadUserContracts } from '../../io/sync/userContracts';
import type { Contract } from '../../selectors/contracts';
import { mergeContractCatalogue, type UserContract } from '../../selectors/userContracts';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type ContractsPhase = 'loading' | 'ready' | 'failed';

export interface ContractsState {
  phase: ContractsPhase;
  /**
   * The catalogue everything measures with: shared, with the trader's own rows
   * laid over it. ⚠ NULL until the SHARED read succeeded.
   */
  contracts: readonly Contract[] | null;
  /**
   * Just the trader's own rows — what the Contract Library screen edits.
   *
   * ⚠ Empty is legitimate and common. It is NOT evidence the read worked; check
   * `customError` for that.
   */
  custom: readonly UserContract[];
  error: string | null;
  /** Non-null when the CUSTOM read failed. The shared catalogue is still live. */
  customError: string | null;
  reload: () => void;
}

const Ctx = createContext<ContractsState | null>(null);

export function useContracts(): ContractsState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useContracts() outside <ContractsProvider>');
  return v;
}

export function ContractsProvider(
  { sb, children }: { sb: SupabaseClient; children: ReactNode },
) {
  const [phase, setPhase] = useState<ContractsPhase>('loading');
  const [shared, setShared] = useState<readonly Contract[] | null>(null);
  const [custom, setCustom] = useState<readonly UserContract[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [customError, setCustomError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      // ⚠ SEQUENTIAL, not Promise.all. The custom read is worthless without the
      // shared one — there would be nothing to lay it over — and firing both
      // means a second request every time the catalogue is unreadable anyway.
      try {
        const rows = await loadContracts(sb);
        if (cancelled) return;
        setShared(rows);
      } catch (e) {
        if (cancelled) return;
        // ⚠ stays NULL — never []. An empty catalogue prices every excursion at
        // $0 and drops tick-denominated trades out of the totals, both silently.
        setError(reportFailure('contracts', e));
        setPhase('failed');
        return;
      }

      const mine = await loadUserContracts(sb);
      if (cancelled) return;
      setCustom(mine.contracts);
      setCustomError(mine.error);
      setPhase('ready');
    })();

    return () => { cancelled = true; };
  }, [sb, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // ⚠ Memoised on the two ARRAYS, and `mergeContractCatalogue` returns the
  // shared array unchanged when the trader has no custom rows — so for almost
  // every user this is the same reference every render, and nothing downstream
  // re-derives.
  const contracts = useMemo(
    () => (shared ? mergeContractCatalogue(shared, custom as readonly Contract[]) : null),
    [shared, custom],
  );

  const value = useMemo<ContractsState>(
    () => ({ phase, contracts, custom, error, customError, reload }),
    [phase, contracts, custom, error, customError, reload],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
